import {
  CalendarRange,
  DateHelper,
  PrimaryButtonLink,
  QuantitySelectorStyled,
  SelectBox,
  createDate,
  openDatesFromOpeningHours,
  useOpeningHours,
  exhaustiveCheck,
} from "@parksandresorts/core";
import { Script, graphql } from "gatsby";
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import styles from "./first-camp-block.module.less";

type TranslationStrings =
  | "ARRIVAL_LABEL"
  | "ARRIVAL_PLACEHOLDER"
  | "DEPARTURE_LABEL"
  | "DEPARTURE_PLACEHOLDER"
  | "GUEST_QUANTITY_LABEL"
  | "GUEST_QUANTITY_DESCRIPTION"
  | "GUEST_TYPE_ADULT"
  | "GUEST_TYPE_CHILD"
  | "GUEST_TYPE_BABY"
  | "ACCOMMODATION_TYPE_LABEL"
  | "ACCOMMODATION_TYPE_CARAVAN"
  | "ACCOMMODATION_TYPE_MOTORHOME"
  | "ACCOMMODATION_TYPE_CABIN"
  | "ACCOMMODATION_TYPE_TENT"
  | "SEARCH_BUTTON_LABEL";

type TranslationString = `FIRST_CAMP_${TranslationStrings}`;
const t = (key: TranslationString): string | undefined => SiteConfigurationStore.getLabel(key) || undefined;

type DateRange = { from?: Date; to?: Date };

type CsobOptions = {
  csobPortalKey: string;
  csobHost: string;
  profileListLayout: number;
};

type Props = {
  node_locale: string;
  contentful_id: string;
  csobOptions: {
    internal: {
      content: string;
    };
  };
};

type CSOBControllerOptions = {
  csobPortalKey: string;
  csobHost: string;
  profileListLayout: number;
  profileListWrapper: string;
  errorPanelWrapper: string;
  cartWrapper: string;
  loginWrapper: string;
  onFinishedCallback: () => void;
};

type CSOB = {
  Controller: (options: CSOBControllerOptions) => string;
};

const livingForms = ["caravan", "motorhome", "cabin", "tent"] as const;
type LivingForm = (typeof livingForms)[number];

const validPackageIds = ["44,56", "45", "46,55", "47"] as const;
type PackageId = (typeof validPackageIds)[number];

const stringIsValidPackageId = (value: string): value is PackageId => {
  return validPackageIds.includes(value as PackageId);
};

const stringIsValidLivingForm = (value: string): value is LivingForm => {
  return livingForms.includes(value as LivingForm);
};

const getTypesFromPackageId = (packageId: PackageId): Array<LivingForm> => {
  switch (packageId) {
    case "44,56":
    case "45":
      return ["cabin"];
    case "46,55":
    case "47":
      return ["caravan", "motorhome", "tent"];
    default:
      exhaustiveCheck(packageId);
  }
};

const getLivingFormByPackageId = (packageId: PackageId, livingForm?: string | null) => {
  const types = getTypesFromPackageId(packageId);
  if (!livingForm || !stringIsValidLivingForm(livingForm)) {
    return types[0];
  }
  if (!types.includes(livingForm)) {
    return types[0];
  }
  return livingForm;
};

/**
 * There are only two forms of living according to the packages - "stuga" and "camping".
 * The user can still pick between "caravan", "motorhome", "cabin" and "tent" but we need to map these to the correct package form.
 */
const getPackageLivingForm = (livingForm: LivingForm) => {
  switch (livingForm) {
    case "cabin":
      return "stuga";
    case "caravan":
    case "motorhome":
    case "tent":
      return "camping";
    default:
      exhaustiveCheck(livingForm);
  }
};

/**
 * There are 6 different pre-defined packages with number ids for First Camp. We need the amount of nights staying and the living form to get the correct package id.
 * 44: 1dag/1dags biljett SkaraSommarlandspaket Stuga
 * 46: 1dag/1dags biljett SkaraSommarlandspaket Camping
 * 45: 2 dgr Skara Sommarlandspaket Stuga
 * 47: 2 dgr Skara Sommarlandspaket Camping
 * 55: 1dag 2dgr biljetter SkaraSommarlandspaket Camping
 * 56: 1dag 2dgr biljetter SkaraSommarlandspaket Stuga
 */
const getPackageId = (arrivalDate: Date, departureDate: Date, livingForm: LivingForm) => {
  const amountOfNightsStaying: number = DateHelper.fullDaysBetweenDates(arrivalDate, departureDate);
  const packageAmountOfNights = amountOfNightsStaying >= 2 ? 2 : 1; // If staying 2 or more nights, use 2 nights package
  const livingFormCSOBPackage = getPackageLivingForm(livingForm);

  const packageList = {
    stuga: {
      1: "44,56",
      2: "45",
    },
    camping: {
      1: "46,55",
      2: "47",
    },
  } as const;

  return packageList[livingFormCSOBPackage][packageAmountOfNights] satisfies PackageId;
};

const areAllValuesSet = (
  adultQuantity: number,
  shortChildrenQuantity: number,
  tallChildrenQuantity: number,
  arrivalDate?: Date,
  departureDate?: Date,
  livingForm?: LivingForm,
) => {
  return arrivalDate && departureDate && livingForm && adultQuantity + shortChildrenQuantity + tallChildrenQuantity > 0;
};

const generateUrl = (
  lang: string,
  adultQuantity: number,
  shortChildrenQuantity: number,
  tallChildrenQuantity: number,
  arrivalDate?: Date,
  departureDate?: Date,
  livingForm?: LivingForm,
) => {
  if (
    !areAllValuesSet(adultQuantity, shortChildrenQuantity, tallChildrenQuantity, arrivalDate, departureDate, livingForm)
  )
    return "javascript:void(0)"; // We don't want to change the URL (and scroll to the top) when the button is disabled

  lang = lang.toLocaleUpperCase();
  const arrivalDateString = DateHelper.toStringDDMMYYYY(arrivalDate);
  const departureDateString = DateHelper.toStringDDMMYYYY(departureDate);
  const packageId = getPackageId(arrivalDate!, departureDate!, livingForm!);
  const url = `?livingForm=${livingForm}&from=${arrivalDateString}&to=${departureDateString}&siteid=SKARA&lang=${lang}&VARS=AV_${adultQuantity},AB_${tallChildrenQuantity},AS_${shortChildrenQuantity}&portal=2&cbpid=${packageId}`;
  return url;
};

const FirstCampBlock = ({ node_locale, contentful_id, csobOptions: csobOptionsRaw }: Props) => {
  const scrollResultRef = useRef<HTMLDivElement>(null);
  const csobOptions: CsobOptions = JSON.parse(csobOptionsRaw?.internal?.content || "{}");

  const onLoadCsob = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (csob && typeof csob.Controller === "function") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      (csob as CSOB).Controller({
        ...csobOptions,
        profileListWrapper: "#searchResult",
        errorPanelWrapper: "#errorResult",
        cartWrapper: "#cart",
        loginWrapper: "#loginWrapper",
      });
    }
  };

  //on mount, read from url and set state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const livingForm = urlParams.get("livingForm");
    const from = urlParams.get("from");
    const to = urlParams.get("to");
    const packageId = urlParams.get("cbpid");
    const vars = urlParams.get("VARS");

    let dateExistInUrl = false;
    let packageIdExistInUrl = false;

    if (vars && vars.includes("AV_")) {
      const av = vars.split("AV_")[1].split(",")[0];
      setAdultQuantity(parseInt(av));
    }

    if (vars && vars.includes("AB_")) {
      const ab = vars.split("AB_")[1].split(",")[0];
      setTallChildrenQuantity(parseInt(ab));
    }

    if (vars && vars.includes("AS_")) {
      const ab = vars.split("AS_")[1].split(",")[0];
      setShortChildrenQuantity(parseInt(ab));
    }

    const dates: DateRange = {};
    if (from && DateHelper.isDateValid(from, "DD-MM-YYYY")) {
      dates.from = createDate(from, "DD-MM-YYYY");
      setDisplayMonth(createDate(from, "DD-MM-YYYY"));
    }
    if (dates.from && to && DateHelper.isDateValid(to, "DD-MM-YYYY")) {
      dates.to = createDate(to, "DD-MM-YYYY");
      dateExistInUrl = true;
    }
    setDates(dates);
    if (packageId && stringIsValidPackageId(packageId)) {
      const type = getLivingFormByPackageId(packageId, livingForm);
      setLivingForm(type);
      packageIdExistInUrl = true;
    }

    if (dateExistInUrl && packageIdExistInUrl) {
      setSearchIsBeingPerformed(true);
    }
  }, []);

  const [openTooltip, setOpenTooltip] = useState<"quantity" | "dates" | null>(null);

  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [dates, setDates] = useState<DateRange>();
  const [livingForm, setLivingForm] = useState<LivingForm>();
  const [adultQuantity, setAdultQuantity] = useState<number>(1);
  const [shortChildrenQuantity, setShortChildrenQuantity] = useState<number>(0);
  const [tallChildrenQuantity, setTallChildrenQuantity] = useState<number>(0);

  const [searchIsBeingPerformed, setSearchIsBeingPerformed] = useState(false);
  const openingHours = useOpeningHours();

  const { from: arrivalDate, to: departureDate } = dates ?? {};
  const { startDate, endDate, disabledDates } = useMemo(
    () => openDatesFromOpeningHours(openingHours, 1),
    [openingHours],
  );

  const closeAllTooltips = () => setOpenTooltip(null);
  const changeDates = (d: DateRange) => {
    setDates(d);
    if (d?.to) closeAllTooltips();
  };

  useEffect(() => {
    if (!searchIsBeingPerformed || !scrollResultRef?.current || typeof window === "undefined") return;

    // Workaround because the window automatically scrolls to the top after navigating
    scrollResultRef.current.scrollIntoView?.();
  }, [scrollResultRef, searchIsBeingPerformed]);

  const allValuesSet = areAllValuesSet(
    adultQuantity,
    shortChildrenQuantity,
    tallChildrenQuantity,
    arrivalDate,
    departureDate,
    livingForm,
  );
  const url = generateUrl(
    node_locale,
    adultQuantity,
    shortChildrenQuantity,
    tallChildrenQuantity,
    arrivalDate,
    departureDate,
    livingForm,
  );

  return (
    <section data-contentful-id={contentful_id} className="block block--floating block--max-width-extra">
      {searchIsBeingPerformed && (
        <main>
          <Script
            src="https://v3.onlinebooking.dk/csobV2/csob.controller.min.js?bfversion=1.0.1"
            onLoad={() => onLoadCsob()}
            onError={(error) => console.error(error)}
          />
          <Script src="https://code.jquery.com/jquery-2.2.4.min.js" />
          <link rel="stylesheet" type="text/css" href="/first-camp/csob-overrides.css" />
        </main>
      )}
      <div className={styles.searchHeader}>
        <div
          className={styles.headerInput}
          data-contentful-label-id={`FIRST_CAMP_ARRIVAL_LABEL\nFIRST_CAMP_ARRIVAL_PLACEHOLDER`}
        >
          <TooltipButton
            label={t("FIRST_CAMP_ARRIVAL_LABEL") ?? "Ankomst"}
            value={
              arrivalDate
                ? DateHelper.toStringDDShortMonth(arrivalDate)
                : t("FIRST_CAMP_ARRIVAL_PLACEHOLDER") ?? "Lägg till datum"
            }
            onClick={() => {
              setOpenTooltip("dates");
            }}
          />
          <Tooltip isOpen={openTooltip === "dates"} close={() => closeAllTooltips()} mobilePosition="left">
            <CalendarRange
              startDate={startDate}
              endDate={endDate}
              disabledDates={disabledDates}
              selectedDates={dates?.from && { from: dates.from, to: dates.to }} // Strange type error fix
              changeDateRange={(d) => d && changeDates(d)} // Less strange type error fix
              resetIfRangeExists={true}
              changedMonth={setDisplayMonth}
              currentDisplayedMonth={displayMonth}
            />
          </Tooltip>
        </div>
        <div
          className={styles.headerInput}
          data-contentful-label-id={`FIRST_CAMP_DEPARTURE_LABEL\nFIRST_CAMP_DEPARTURE_PLACEHOLDER`}
        >
          <TooltipButton
            label={t("FIRST_CAMP_DEPARTURE_LABEL") ?? "Avresa"}
            value={
              departureDate
                ? DateHelper.toStringDDShortMonth(departureDate)
                : t("FIRST_CAMP_DEPARTURE_PLACEHOLDER") ?? "Lägg till datum"
            }
            onClick={() => {
              setOpenTooltip("dates");
            }}
          />
        </div>
        <div
          className={`${styles.headerInput}`}
          data-contentful-label-id={`FIRST_CAMP_GUEST_QUANTITY_LABEL\nFIRST_CAMP_GUEST_QUANTITY_DESCRIPTION`}
        >
          <TooltipButton
            label={t("FIRST_CAMP_GUEST_QUANTITY_LABEL") ?? "Gäster"}
            value={(t("FIRST_CAMP_GUEST_QUANTITY_DESCRIPTION") ?? "{0} gäster").replaceAll(
              "{0}",
              (adultQuantity + shortChildrenQuantity + tallChildrenQuantity).toString(),
            )}
            onClick={() => {
              setOpenTooltip("quantity");
            }}
          />

          <Tooltip isOpen={openTooltip === "quantity"} close={() => closeAllTooltips()} mobilePosition="left">
            <div className={styles.quantityWrapper} data-contentful-label-id="FIRST_CAMP_GUEST_TYPE_{ADULT/CHILD/BABY}">
              <QuantitySelectorStyled
                label={t("FIRST_CAMP_GUEST_TYPE_ADULT") ?? "Vuxen (från 12 år)"}
                quantity={adultQuantity}
                onQuantityChange={(newQuantity) => {
                  setAdultQuantity(newQuantity);
                }}
              />
              <QuantitySelectorStyled
                label={t("FIRST_CAMP_GUEST_TYPE_CHILD") ?? "Barn över 1 meter"}
                quantity={tallChildrenQuantity}
                onQuantityChange={(newQuantity) => {
                  setTallChildrenQuantity(newQuantity);
                }}
              />
              <QuantitySelectorStyled
                label={t("FIRST_CAMP_GUEST_TYPE_BABY") ?? "Barn under 1 meter"}
                quantity={shortChildrenQuantity}
                onQuantityChange={(newQuantity) => {
                  setShortChildrenQuantity(newQuantity);
                }}
              />
            </div>
          </Tooltip>
        </div>
        <div
          className={`${styles.headerInput} ${styles.selectWrapper}`}
          data-contentful-label-id={`FIRST_CAMP_ACCOMMODATION_TYPE_LABEL\nFIRST_CAMP_ACCOMMODATION_TYPE_{CARAVAN/MOTORHOME/CABIN/TENT}`}
        >
          <SelectBox
            notSelectedText={t("FIRST_CAMP_ACCOMMODATION_TYPE_LABEL") ?? "Välj boendeform"}
            value={livingForm}
            options={
              [
                { value: "caravan", label: t("FIRST_CAMP_ACCOMMODATION_TYPE_CARAVAN") ?? "Husvagn" },
                { value: "motorhome", label: t("FIRST_CAMP_ACCOMMODATION_TYPE_MOTORHOME") ?? "Husbil" },
                { value: "cabin", label: t("FIRST_CAMP_ACCOMMODATION_TYPE_CABIN") ?? "Stuga" },
                { value: "tent", label: t("FIRST_CAMP_ACCOMMODATION_TYPE_TENT") ?? "Tält" },
              ] satisfies Array<{ value: LivingForm; label: string }>
            }
            onChange={(_name, value) => setLivingForm(value)}
            onOpen={() => closeAllTooltips()}
          />
        </div>
        <div className={styles.headerInput} data-contentful-label-id="FIRST_CAMP_SEARCH_BUTTON_LABEL">
          <PrimaryButtonLink
            isDisabled={!allValuesSet}
            isFullWidth={true}
            url={url}
            label={t("FIRST_CAMP_SEARCH_BUTTON_LABEL") ?? "Visa priser"}
          />
        </div>
      </div>
      {searchIsBeingPerformed && (
        <div className={styles.searchResult} ref={scrollResultRef}>
          <div className={`${styles.csob} ${styles.minWidth}`} id="searchResult"></div>
          <div className={styles.csob} id="cart"></div>
          <div className={styles.csob} id="errorResult"></div>
          <div className={`${styles.csob} ${styles.loginWrapper}`} id="loginWrapper"></div>
        </div>
      )}
    </section>
  );
};

const TooltipButton = ({ label, value, onClick }: { label: string; value: string; onClick: () => void }) => {
  return (
    <button className={styles.tooltipButtonTrigger} onClick={() => onClick()}>
      <label className="bold">{label}</label>
      <input value={value} type="button"></input>
    </button>
  );
};

const Tooltip = ({
  isOpen,
  children,
  close,
  mobilePosition,
}: PropsWithChildren<{ isOpen: boolean; close: () => void; mobilePosition: "left" | "right" }>) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //@ts-expect-error Typescript complains about event.target not being a Node that can be searched for in contains. But it works
      if (tooltipRef.current && event.target && !tooltipRef.current.contains(event.target)) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen, close]);

  if (isOpen) {
    return (
      <div ref={tooltipRef} className={styles.tooltip} data-tooltip-position={mobilePosition}>
        {children}
      </div>
    );
  }
  return null;
};

export const fragmentTextBlock = graphql`
  fragment FirstCampBlock on ContentfulFirstCampBlock {
    node_locale
    contentful_id
    csobOptions {
      # https://github.com/gatsbyjs/gatsby/issues/14762#issuecomment-506084912
      internal {
        content
      }
    }
  }
`;

export default FirstCampBlock;
